import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, X as CloseIcon, LayoutDashboard, MessageCircle, Settings as SettingsIcon, LogOut } from 'lucide-react';
import { canCheckout, goToCheckout, PLAN_PRICES } from '../lib/billing';
import { MimiqueIcon, MimiqueWordmark, BRAND } from '../components/MimiqueLogo';
import { C, F, CTA_PRIMARY, SiteHeader, SiteFinalCta, SiteFooter } from '../components/SiteChrome';
import FaqList from '../components/FaqList';
import { BlogCarousel } from '../components/BlogCard';
import { Carousel } from '../components/Carousel';
import { fetchPosts } from '../lib/blogApi';
// Shared with the FAQPage schema generated into index.html at build time,
// so the page and the structured data can never disagree.
import faqs from '../data/faqs.json';

// Mirrors BETA_CLOSED in server.py. Keep the two in step: while closed, the
// landing page offers log in only, with no sign-up tab and no checkout
// hand-off, so it matches what the backend will actually allow.
const BETA_CLOSED = false;

// `highlight` marks the words a skim-reader should land on. Each entry has to
// appear in the quote exactly as written; nothing is paraphrased, because
// these are other people's words.
export const testimonials = [
    { quote: 'I have been doing Duolingo for three years and have found Mimique much better for me as I have trouble retaining phrases, I have remembered much more since starting Mimique.', name: 'Martin H.', highlight: ['much better for me', 'remembered much more'] },
    { quote: 'A chance to concentrate on pronunciation, with good feedback, much better than other apps. I think it is unrealistic to think the launch product should be perfect, it is on a journey as I am on a language journey.', name: 'David P.', highlight: ['concentrate on pronunciation', 'much better than other apps'] },
    { quote: 'What I like about Mimique: Repetition of phrases I need; confidence after hearing how I say something and comparing to how a native says it. I am more confident, and can have actual short conversations. It\'s a really great app, definitely the best out there.', name: 'Tracy P.', highlight: ['I am more confident', 'actual short conversations', 'the best out there'] },
    { quote: 'I love the affirmation when my timing and accent are correct and helpful advice about where I get it wrong. This last bit is most significant as no other app has done this.', name: 'Marilyn A.', highlight: ['where I get it wrong', 'no other app has done this'] },
    { quote: 'The pronunciation tool is awesome. It\'s really helpful to hear how I am pronouncing the words. Really well done on Mimique, it\'s a great way to learn French!', name: 'Jenlee R.', highlight: ['The pronunciation tool is awesome', 'a great way to learn French'] },
    { quote: 'It really helps with learning the speech patterns when talking French. I feel this could also aid people with dyslexia as there isn\'t too much typing, just lots of talking and listening.', name: 'Poppy A.', highlight: ['learning the speech patterns', 'aid people with dyslexia'] },
    { quote: 'I feel that I am now pronouncing words as they should be heard. Before I had no clarity of the sounds needed. Phonics help me as well as listening.', name: 'Tina H.', highlight: ['pronouncing words as they should be heard'] },
    { quote: 'This is great and different from the other apps I\'ve used. Well done.', name: 'Bill B.', highlight: ['great and different from the other apps'] },
    { quote: 'I haven\'t come across a resource that has been as useful as this in hearing my own inaccuracies regarding pronunciation. I do believe it could be very helpful in learning French.', name: 'Eddie G.', highlight: ['hearing my own inaccuracies'] },
    { quote: 'I\'m very fond of the app. I feel I\'m not so fumbling and bumbling over the words, which of course inspires confidence. My brain is adjusting to French. I like to add my own phrases, so that I learn to say them without thinking.', name: 'Bryn D.', label: '30-Day Challenge Member', highlight: ['not so fumbling and bumbling over the words', 'My brain is adjusting to French'] },
];

// What sits under a name. Everyone before the 30-day challenge was a Founding
// Member, so that stays the default; an entry says otherwise with `label`.
const DEFAULT_TESTIMONIAL_LABEL = 'Founding Member';

/** Five stars, the shorthand everyone reads before they read anything else. */
const Stars = () => (
    <div aria-hidden="true" style={{ display: 'flex', gap: 3 }}>
        {Array.from({ length: 5 }, (_, i) => (
            <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#e0a83c">
                <path d="M12 2.5l2.9 5.9 6.5.9-4.7 4.6 1.1 6.4-5.8-3-5.8 3 1.1-6.4L2.6 9.3l6.5-.9z" />
            </svg>
        ))}
    </div>
);

/**
 * A quote with its key phrases picked out.
 *
 * The phrases are matched literally, so a stray bracket or full stop in one
 * cannot break the regex, and the comparison is case-insensitive only so the
 * marked-up text keeps the writer's own capitals.
 */
const QuoteText = ({ quote, highlight = [] }) => {
    if (!highlight.length) return quote;
    const escaped = highlight.map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const parts = quote.split(new RegExp(`(${escaped.join('|')})`, 'gi'));
    const wanted = highlight.map((h) => h.toLowerCase());
    return parts.map((part, i) => (
        wanted.includes(part.toLowerCase())
            ? <mark key={i} className="mq-quotemark">{part}</mark>
            : <span key={i}>{part}</span>
    ));
};

// Ordered most to least important to the audience Mimique is aimed at, so the
// first row on desktop is the core niche. Holidaymakers sit last deliberately:
// they are the most casual use, and leading with them would undercut the
// everyday-life positioning.
const whoItsFor = [
    { title: 'Expats and immigrants', body: 'Practise the words and phrases you need for everyday life, from the boulangerie to the doctor, the bank or work.' },
    { title: 'Shy or nervous speakers', body: 'Build confidence by practising privately before you use your French in real conversations. No more freezing when speaking!' },
    { title: 'Pronunciation improvers', body: 'Focus on the sounds you struggle with, practise them repeatedly and learn to speak more clearly.' },
    { title: 'Self-directed learners', body: "Choose exactly what you want to practise. No prescribed lessons or vocabulary lists you don't need." },
    { title: 'Beginners to advanced', body: "Whether you're starting from scratch or already speak French, better pronunciation is essential to be understood." },
    { title: 'Holidaymakers', body: 'Learn useful words and phrases before your trip and feel more confident using them when you arrive.' },
];


// ── Phone mockup ─────────────────────────────────────────────────────────────
// Shows a real dashboard screenshot when one is present at the path below,
// falling back to the Listen / Repeat / Compare cycle graphic if it's missing
// so the hero never renders a broken image.
const SCREENSHOT_SRC = '/assets/dashboard-mobile.jpg';
const DEMO_VIDEO_SRC = '/assets/mimique-demo-phone.mp4';

export const PhoneMockup = () => {
    const [shotOk, setShotOk] = useState(true);
    const [videoOk, setVideoOk] = useState(true);
    return (
    <div style={{ width: 280, height: 570, borderRadius: 44, background: '#0f2417', border: '8px solid #0a1a10', boxShadow: '0 30px 70px -20px rgba(0,0,0,0.55)', padding: 14, position: 'relative' }}>
        <div style={{ width: '100%', height: '100%', borderRadius: 30, overflow: 'hidden', background: '#ffffff', position: 'relative' }}>
            {shotOk && videoOk && (
                <video
                    src={DEMO_VIDEO_SRC}
                    poster={SCREENSHOT_SRC}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    aria-label="The Mimique app in action on a phone"
                    onError={() => setVideoOk(false)}
                    style={{ position: 'absolute', inset: '0 0 52px 0', width: '100%', height: 'calc(100% - 52px)', boxSizing: 'border-box', paddingTop: 18, objectFit: 'contain', objectPosition: 'bottom center', background: 'linear-gradient(#16231b 18px, #426c4e)', zIndex: 4 }}
                />
            )}
            {/* The recording is a little shorter than the frame, so the app's
                own bottom nav fills the space under it. */}
            {shotOk && videoOk && (
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 52, background: '#ffffff', borderTop: '1px solid #e7e5e4', display: 'flex', zIndex: 5 }}>
                    {[
                        { label: 'Dashboard', Icon: LayoutDashboard, active: true },
                        { label: 'Phrases', Icon: MessageCircle },
                        { label: 'Settings', Icon: SettingsIcon },
                        { label: 'Sign Out', Icon: LogOut },
                    ].map(({ label, Icon, active }) => (
                        <div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, color: active ? '#2d7b4b' : '#a8a29e' }}>
                            <Icon size={18} />
                            <span style={{ fontSize: 9, fontWeight: 500, lineHeight: 1 }}>{label}</span>
                        </div>
                    ))}
                </div>
            )}
            {shotOk && !videoOk && (
                <img
                    src={SCREENSHOT_SRC}
                    alt="The Mimique dashboard on a phone"
                    onError={() => setShotOk(false)}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', zIndex: 4 }}
                />
            )}
            {/* The screenshot carries its own logo, so only show this one on the
                fallback graphic. */}
            {!shotOk && <MimiqueIcon size={44} variant="bare" onLight style={{ position: 'absolute', top: 12, left: 12, zIndex: 5 }} />}
            <svg viewBox="0 0 248 500" width="100%" height="100%">
                <circle cx="124" cy="236" r="90" fill="none" stroke={C.deep} strokeWidth="1" opacity="0.08" />
                <path d="M139.6 147.3 A90 90 0 0 1 208.6 266.8" fill="none" stroke={C.bright} strokeWidth="5" strokeLinecap="round" />
                <path d="M192.9 293.9 A90 90 0 0 1 55.1 293.9" fill="none" stroke={C.bright} strokeWidth="5" strokeLinecap="round" />
                <path d="M39.4 266.8 A90 90 0 0 1 108.4 147.3" fill="none" stroke={C.bright} strokeWidth="5" strokeLinecap="round" />
                <path d="M208.6 266.8 L196.2 261.4 L206.7 253.1 Z" fill={C.bright} />
                <path d="M55.1 293.9 L64.3 304.8 L50.4 305.9 Z" fill={C.bright} />
                <path d="M108.4 147.3 L118.6 155.5 L104.9 159.9 Z" fill={C.bright} />
                <circle cx="124" cy="146" r="7" fill={C.deep} />
                <circle cx="201.9" cy="281" r="7" fill={C.deep} />
                <circle cx="46.1" cy="281" r="7" fill={C.deep} />
                <text x="124" y="122" textAnchor="middle" fontFamily={F.heading} fontWeight="800" fontSize="25" fill={C.deep}>Listen</text>
                <text x="201.9" y="312" textAnchor="middle" fontFamily={F.heading} fontWeight="800" fontSize="25" fill={C.deep}>Repeat</text>
                <text x="46.1" y="312" textAnchor="middle" fontFamily={F.heading} fontWeight="800" fontSize="25" fill={C.deep}>Compare</text>
            </svg>
        </div>
        {/* No notch bar: it sat over the app header in the screenshot. */}
    </div>
    );
};

// ── Small pieces ─────────────────────────────────────────────────────────────

// `mb` is overridable because the About block is a flex column with its own
// gap, so the default bottom margin stacks on top of it and reads as a much
// bigger gap than every other eyebrow/heading pair on the page.
export const SectionEyebrow = ({ children, onDark = false, mb = 16 }) => (
    <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: onDark ? C.light : C.primary, marginBottom: mb }}>
        {children}
    </div>
);

export const StepCard = ({ n, title, body, iconPath }) => (
    <div style={{
        background: '#ffffff',
        border: `1px solid ${C.borderLight}`,
        borderRadius: 28,
        padding: '32px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
    }}>
        {/* Icon and step number share a row so they sit on the same line */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ width: 52, height: 52, flexShrink: 0, borderRadius: '50%', background: C.bright, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">{iconPath}</svg>
            </div>
            <div style={{ fontFamily: F.heading, fontSize: 40, fontWeight: 800, color: C.primary, opacity: 0.16, lineHeight: 1 }}>
                {n}
            </div>
        </div>
        <div style={{ fontFamily: F.brand, fontSize: 20, fontWeight: 700, color: C.deep }}>{title}</div>
        <div style={{ fontFamily: F.body, fontSize: 15.5, lineHeight: 1.7, color: C.bodyOnWhite2 }}>{body}</div>
    </div>
);

const TierCard = ({ name, price, priceNote, features, cta, onCta, featured = false, note }) => (
    <div className={`mq-lift${featured ? ' mq-lift--strong' : ''}`} style={{
        background: featured ? `linear-gradient(160deg, ${C.mid} 0%, ${C.deep} 100%)` : '#ffffff',
        border: featured ? 'none' : `1px solid ${C.borderLight2}`,
        borderRadius: 32,
        padding: 40,
        display: 'flex',
        flexDirection: 'column',
        gap: 22,
        position: 'relative',
        boxShadow: featured ? '0 24px 48px -18px rgba(28,76,49,0.35)' : 'none',
    }}>
        {featured && (
            <div style={{ position: 'absolute', top: -14, right: 32, background: C.onDarkPrimary, color: C.deep, fontSize: 11.5, fontWeight: 800, padding: '6px 14px', borderRadius: 999, fontFamily: F.brand }}>
                RECOMMENDED
            </div>
        )}
        <div style={{ fontFamily: F.brand, fontSize: 20, fontWeight: 700, color: featured ? '#fff' : C.deep }}>{name}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontFamily: F.brand, fontSize: 42, fontWeight: 800, color: featured ? '#fff' : C.deep }}>{price}</span>
            <span style={{ fontSize: 14, color: featured ? 'rgba(255,255,255,0.7)' : C.bodyOnWhite }}>{priceNote}</span>
        </div>
        {note && (
            <div style={{ fontSize: 12.5, color: featured ? 'rgba(255,255,255,0.75)' : C.bodyOnWhite }}>{note}</div>
        )}
        <div style={{ height: 1, background: featured ? 'rgba(255,255,255,0.16)' : C.borderLight2 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13, fontSize: 14.5, color: featured ? C.onDarkPrimary : C.bodyOnWhite }}>
            {features.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true">
                        <circle cx="12" cy="12" r="11" fill={featured ? 'rgba(255,255,255,0.18)' : '#e3f3e8'} />
                        <path d="M7 12.5l3 3 7-7" stroke={featured ? '#ffffff' : C.bright} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>{f}</span>
                </div>
            ))}
        </div>
        {cta && (
        <button
            className="mq-cta mq-arrowbtn"
            onClick={onCta}
            style={featured
                ? { ...CTA_PRIMARY, marginTop: 8, textAlign: 'center', padding: '15px' }
                : {
                    marginTop: 8,
                    textAlign: 'center',
                    padding: '15px',
                    borderRadius: 9999,
                    border: `1px solid ${C.primary}`,
                    background: 'transparent',
                    color: C.deep,
                    fontFamily: F.body,
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: 'pointer',
                }}
        >
            {cta}
            <span className="mq-arrow" aria-hidden="true">&rarr;</span>
        </button>
        )}
    </div>
);

// ── Main component ───────────────────────────────────────────────────────────

// The testimonial cards, in the shared Carousel: swipe, dots, and a slow
// auto-advance, exactly as the blog section behaves. See components/Carousel.
export const TestimonialCarousel = ({ items }) => {
    // Shuffled once per page load so the first three aren't always the same
    // people. useState's initialiser runs once, so it stays stable across
    // re-renders — reshuffling mid-scroll would swap cards under the reader.
    const [shuffled] = useState(() => {
        const a = [...items];
        for (let i = a.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
    });

    return (
        <div>
            <Carousel label="Testimonials page" intervalMs={0} autoscroll={25}>
                {shuffled.map((t, idx) => <TestimonialCard key={idx} t={t} />)}
            </Carousel>
            <QuoteStyles />
        </div>
    );
};

/** One quote card, as on the home page. Shared with the challenge page. */
export const TestimonialCard = ({ t }) => (
    <div className="mq-quotecard mq-lift" style={{ background: '#ffffff', border: `1px solid ${C.borderLight}`, borderRadius: 28, padding: '28px 30px 30px', display: 'flex', flexDirection: 'column', gap: 14, height: '100%', position: 'relative', overflow: 'hidden' }}>
        {/* Oversized opening quote mark, per the design. It
            sits behind the text now rather than taking a
            line of its own, which buys room for the stars. */}
        <div aria-hidden="true" style={{ position: 'absolute', top: -18, right: 14, fontFamily: F.heading, fontSize: 130, fontWeight: 800, color: C.paleBg, lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>
            &rdquo;
        </div>
        <Stars />
        <div style={{ fontSize: 15.5, lineHeight: 1.75, color: C.deep, position: 'relative' }}>
            <QuoteText quote={t.quote} highlight={t.highlight} />
        </div>
        <div style={{ marginTop: 'auto', paddingTop: 14, borderTop: `1px solid ${C.borderLight}`, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div aria-hidden="true" style={{ width: 38, height: 38, borderRadius: 999, background: C.faqBg, border: `1px solid ${C.faqBorder}`, color: C.primary, fontFamily: F.brand, fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {t.name.charAt(0)}
            </div>
            <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.deep }}>{t.name}</div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: C.primary }}>{t.label || DEFAULT_TESTIMONIAL_LABEL}</div>
            </div>
        </div>
    </div>
);

/** The quote card's styles. Rendered once wherever cards are shown. */
export const QuoteStyles = () => (
    <style>{`
        /* The phrases worth reading if you read nothing else. Kept to a
           tint rather than a solid colour so a card with three of them
           still reads as a sentence. */
        .mq-quotemark {
            background: #dff0e4;
            color: ${C.deep};
            font-weight: 600;
            padding: 1px 3px;
            margin: 0 -1px;
            border-radius: 5px;
            box-decoration-break: clone;
            -webkit-box-decoration-break: clone;
        }
        /* Cards are equal height in a row, so the shadow is on the card
           rather than the slide, which is only ever a spacer. */
        .mq-quotecard { box-shadow: 0 10px 30px -22px rgba(10,22,16,0.35); }
    `}</style>
);

// `initialPosts` is only given by the build-time render (scripts/prerender.js),
// so the blog cards are in the HTML crawlers read. In the browser it is absent
// and the posts are fetched as before.
const Landing = ({ initialPosts }) => {
    const [isAuthOpen, setIsAuthOpen] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [authView, setAuthView] = useState('tabs');    // 'tabs' | 'forgot' | 'forgot-sent'
    const [authTab, setAuthTab] = useState('login');     // 'login' | 'signup'
    const { user, login, register } = useAuth();
    const navigate = useNavigate();

    const [loginForm, setLoginForm] = useState({ email: '', password: '' });
    const [signupForm, setSignupForm] = useState({ name: '', email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    // Set when someone picks a paid tier while logged out. They make an account
    // first (the Stripe webhook matches payments to users by email), then get
    // sent straight to checkout with that email prefilled.
    const [pendingPlan, setPendingPlan] = useState(null);

    const openAuth = (tab, plan = null) => {
        // Anyone already logged in can visit this page; for them every sign up
        // or login button means "take me into the app", and a paid tier goes
        // straight to checkout the way it does from inside the app.
        if (user) {
            if (plan && canCheckout()) goToCheckout(plan, user.email, user.id);
            else navigate('/dashboard');
            return;
        }
        // canCheckout() is false in the Play Store app, where selling anything
        // is against Play's Payments policy. Signing up still works, it just
        // lands on the dashboard instead of Stripe.
        setPendingPlan(BETA_CLOSED || !canCheckout() ? null : plan);
        setAuthTab(BETA_CLOSED ? 'login' : tab);
        setAuthView('tabs');
        setIsAuthOpen(true);
    };

    // Where to go once auth succeeds: Stripe if they came via a paid tier
    // button, otherwise the dashboard.
    const afterAuth = (email, userId) => {
        if (pendingPlan) {
            goToCheckout(pendingPlan, email, userId);
            return;
        }
        navigate('/dashboard');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        try {
            const email = loginForm.email.trim();
            const userData = await login(email, loginForm.password);
            // No "welcome back" toast: the dashboard header already greets them
            // by name, so the toast said the same thing twice, in the way.
            afterAuth(email, userData?.id);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Login failed');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        try {
            const email = signupForm.email.trim();
            const userData = await register(email, signupForm.password, signupForm.name.trim());
            toast.success('Welcome to Mimique!');
            afterAuth(email, userData?.id);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Sign-up failed');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        try {
            await authApi.forgotPassword(forgotEmail);
            setAuthView('forgot-sent');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleCloseAuth = () => {
        setIsAuthOpen(false);
        setAuthView('tabs');
        setForgotEmail('');
    };

    // The legal pages share this page's header but have no auth modal of their
    // own, so their Log in button sends people here with ?auth=login. The query
    // is stripped once used, to keep it out of the address bar and off any
    // subsequent reload. ?auth=signup does the same for links from outside the
    // site, such as the blog, straight to the sign up tab.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const authParam = params.get('auth');
        if (authParam !== 'login' && authParam !== 'signup') return;
        openAuth(authParam);
        window.history.replaceState({}, '', window.location.pathname);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Every published post, shown in the blog carousel. They live in the
    // database and are edited at /admin/blog, so they are fetched rather than
    // bundled; the section renders nothing until they arrive, and nothing at
    // all if the request fails, which leaves the rest of the page untouched.
    const [latestPosts, setLatestPosts] = useState(initialPosts || []);
    useEffect(() => {
        let live = true;
        fetchPosts()
            .then((posts) => {
                // Newest first by date, not by the admin position number.
                const byDate = [...posts].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
                if (live) setLatestPosts(byDate);
            })
            .catch(() => { /* the blog section simply stays hidden */ });
        return () => { live = false; };
    }, []);

    // Scroll reveal. Each selector is a group whose matches fade and rise into
    // place the first time they scroll into view, staggered in document order
    // so cards arrive one after another rather than all at once. Grids are
    // excluded from their parent group (:not) so a card animates once, not
    // twice.
    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') return undefined;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

        const groups = [
            '.mq-hero-copy > *',
            '#steps .mq-railpad > *:not(.mq-stepgrid)',
            '.mq-stepgrid > *',
            '#features > div > *:not(.mq-three-up)',
            '.mq-three-up > *',
            '#testimonials > div > *',
            '.mq-about > *',
            '#pricing > div > *:not(.mq-tiergrid)',
            '.mq-tiergrid > *',
            '#faq > div > *:not(.mq-faqlist)',
            '.mq-faqlist > *',
            // The blog cards are fetched, so they are not in the DOM when this
            // runs. They are left out rather than re-running the observer,
            // which would strip .mq-in from everything already faded in.
            '.mq-finalcta > *',
        ];

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                entry.target.classList.add('mq-in');
                observer.unobserve(entry.target);
            });
        }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });

        const seen = [];
        groups.forEach((selector) => {
            document.querySelectorAll(selector).forEach((el, i) => {
                el.classList.add('mq-reveal');
                // Capped so a long list never leaves the last item waiting.
                el.style.transitionDelay = `${Math.min(i, 5) * 90}ms`;
                seen.push(el);
            });
        });

        // Observing is held back until the hidden start state has actually been
        // painted. Anything already on screen (the hero) would otherwise get
        // .mq-in in the same frame as .mq-reveal, so the browser sees only the
        // end state and snaps it in with no transition at all.
        let frame = requestAnimationFrame(() => {
            frame = requestAnimationFrame(() => {
                seen.forEach((el) => observer.observe(el));
            });
        });

        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            seen.forEach((el) => {
                el.classList.remove('mq-reveal', 'mq-in');
                el.style.transitionDelay = '';
            });
        };
    }, []);

    // Escape closes the auth modal, matching the click-away on the backdrop.
    useEffect(() => {
        if (!isAuthOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') handleCloseAuth(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isAuthOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div id="top" style={{ background: '#ffffff', color: C.deep, fontFamily: F.body }}>
            <style>{`
                /* Smooth scrolling for the nav anchors. scroll-margin-top keeps
                   each target clear of the sticky header it would otherwise
                   land underneath. */
                html { scroll-behavior: smooth; }
                #steps, #features, #pricing, #faq, #testimonials, #blog { scroll-margin-top: 90px; }

                /* Scroll reveal. The class is added by JavaScript, never in the
                   markup, so with JS off or an observer failure the page still
                   renders fully visible rather than blank. */
                .mq-reveal {
                    opacity: 0;
                    transform: translateY(26px);
                    transition: opacity 0.7s cubic-bezier(0.22, 0.61, 0.36, 1),
                                transform 0.7s cubic-bezier(0.22, 0.61, 0.36, 1);
                }
                .mq-reveal.mq-in { opacity: 1; transform: none; }
                @media (prefers-reduced-motion: reduce) {
                    .mq-reveal { opacity: 1; transform: none; transition: none; }
                }

                /* Hover lift on CTAs and nav pill, per the design system. */
                .mq-cta { transition: transform 0.2s ease; display: inline-block; }
                .mq-cta:hover { transform: translateY(-4px) scale(1.05); }
                .mq-navpill { transition: transform 0.2s ease; }
                .mq-navpill:hover { transform: translateY(-3px) scale(1.05); }
                .mq-navlink { transition: color 0.2s ease; }
                .mq-navlink:hover { color: #b8ecce !important; }
                .mq-social { transition: transform 0.2s ease, background 0.2s ease; }
                .mq-social:hover { transform: translateY(-3px); background: rgba(255,255,255,0.12) !important; }

                /* The phone rail sits beside the hero / steps / who-it's-for
                   region and stays sticky across all three. */
                .mq-rail {
                    position: absolute;
                    /* The phone's gap under the header. It used to come from
                       the sticky top alone, which stops applying once anything
                       (the challenge banner) sits above the header. */
                    top: 42px;
                    /* Stops short of the section end so the phone releases with
                       clear space beneath it rather than butting into the next
                       section. */
                    bottom: 90px;
                    right: max(24px, calc(50% - 532px));
                    width: 280px;
                    z-index: 3;
                    pointer-events: none;
                }
                .mq-railpad { padding-right: 360px; }

                /* Who it's for: three per row, dropping to two then one. */
                .mq-three-up { grid-template-columns: repeat(3, minmax(0, 1fr)); }
                @media (max-width: 900px) { .mq-three-up { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
                @media (max-width: 600px) { .mq-three-up { grid-template-columns: minmax(0, 1fr); } }

                /* Arrow that slides in on CTA hover. inline-flex + line-height 1
                   keeps it optically centred against the label rather than
                   sitting on the text baseline. */
                .mq-arrow {
                    display: inline-flex;
                    align-items: center;
                    line-height: 1;
                    max-width: 0;
                    opacity: 0;
                    overflow: hidden;
                    color: currentColor;
                    transform: translateX(-4px);
                    transition: max-width 0.2s ease, opacity 0.2s ease, transform 0.2s ease, margin-left 0.2s ease;
                }
                .mq-arrowbtn:hover .mq-arrow {
                    max-width: 22px;
                    opacity: 1;
                    margin-left: 8px;
                    transform: translateX(0);
                }

                /* Keeps a heading on a single line where there is room, and
                   lets it wrap normally once the screen is too narrow. */
                .mq-oneline { white-space: nowrap; }
                @media (max-width: 780px) {
                    .mq-oneline { white-space: normal; font-size: 30px; }
                }

                /* The inline hero phone is the mobile counterpart to the sticky
                   rail: exactly one of the two is ever visible. */
                .mq-hero-phone-mobile { display: none; }

                /* .mq-motif itself is in SiteChrome, shared with the closing
                   band that the blog posts use as well. */

                @media (max-width: 820px) {
                    .mq-rail { display: none; }
                    .mq-railpad { padding-right: 0; }
                    /* The wrapper is given the phone's scaled height outright
                       and only the frame inside is scaled, so the layout never
                       depends on negative margins and the buttons can't end up
                       under the phone. 570px is the frame's height. */
                    .mq-hero-phone-mobile {
                        display: flex;
                        justify-content: center;
                        width: 100%;
                        margin-top: 0;
                        height: 502px;
                    }
                    .mq-hero-phone-mobile > div {
                        transform: scale(0.88);
                        transform-origin: top center;
                    }
                }

                @media (max-width: 480px) {
                    .mq-hero-phone-mobile { height: 445px; }
                    .mq-hero-phone-mobile > div { transform: scale(0.78); }
                }

                /* Hero breathes on desktop but the top gap is dead space on a
                   phone, especially under a sticky header. */
                /* Headings are set loose for desktop; on a phone the same
                   line-height leaves big gaps inside a wrapped title. */
                @media (max-width: 768px) {
                    h1, h2 { line-height: 1.15 !important; }
                    .mq-hero { padding-top: 40px !important; padding-bottom: 72px !important; }
                    .mq-hero h1 { font-size: 34px !important; line-height: 1.1 !important; }
                    .mq-hero-copy { gap: 20px !important; }
                    /* Centre the hero CTAs on mobile; they read as stranded when
                       left-aligned under centred artwork. */
                    .mq-hero-ctas { justify-content: center; width: 100%; }
                }

                /* About: side-by-side needs real width; stack it below that. */
                .mq-about { grid-template-columns: minmax(0, 0.42fr) minmax(0, 1fr); }
                @media (max-width: 880px) {
                    .mq-about { grid-template-columns: minmax(0, 1fr); gap: 32px !important; }
                    .mq-about-photo { max-width: 320px; }
                }

                /* Section padding tightens on small screens — 130px top and
                   bottom is a lot of dead space on a phone. */
                @media (max-width: 700px) {
                    .mq-section { padding-top: 72px !important; padding-bottom: 72px !important; }
                }
            `}</style>

            <SiteHeader home onLogin={() => openAuth('login')} />

            {/* HERO + HOW IT WORKS + WHO IT'S FOR share one scroll region so the
                phone can stay sticky across all three, as in the design. The
                phone sits in an absolutely positioned rail spanning the region;
                the sections keep their own full-width backgrounds and reserve
                room for it with padding. */}
            <div style={{ position: 'relative' }}>
                <div className="mq-rail" aria-hidden="true">
                    <div style={{ position: 'sticky', top: 110 }}>
                        <PhoneMockup />
                    </div>
                </div>

                {/* HERO */}
                <div className="mq-hero" style={{ background: `linear-gradient(180deg, ${C.primary} 0%, ${C.deep} 100%)`, padding: '90px 24px 130px', position: 'relative', overflow: 'hidden' }}>
                    {/* Oversized logo mark bleeding off the top-right corner.
                        Decorative only, and low enough contrast to sit behind
                        the headline without competing with it. */}
                    <div className="mq-motif mq-motif--hero" aria-hidden="true">
                        <MimiqueIcon size={460} variant="bare" />
                    </div>
                    <div className="mq-railpad" style={{ maxWidth: 1160, margin: '0 auto', position: 'relative', zIndex: 1 }}>
                        <div className="mq-hero-copy" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 30 }}>
                            <h1 style={{ margin: 0, fontFamily: F.heading, fontSize: 'clamp(36px, 5vw, 54px)', lineHeight: 1.15, fontWeight: 700, letterSpacing: '-0.02em', color: '#ffffff' }}>
                                Speak French confidently with{' '}
                                <span style={{ color: BRAND.mint }}>Mimique</span>
                            </h1>
                            {/* An h2 so the page's main phrase sits in a heading, styled
                                exactly as the paragraph it replaced (the global heading
                                font and letter spacing are switched off here). */}
                            <h2 style={{ margin: 0, fontFamily: 'inherit', letterSpacing: 'normal', fontSize: 20, fontWeight: 500, color: C.onDarkPrimary }}>
                                The French speaking and pronunciation practice app for the words and phrases you actually need.
                            </h2>

                            {/* The sticky rail is desktop-only, which left the
                                mobile hero as text alone. Show the phone inline
                                here instead so small screens still lead with the
                                product. */}
                            <div className="mq-hero-phone-mobile" aria-hidden="true">
                                <PhoneMockup />
                            </div>

                            <p style={{ margin: 0, fontSize: 17, lineHeight: 1.7, color: C.onDarkMuted }}>
                                Mimique goes back to the basics of language learning: <em>listen and repeat</em>. It puts the focus on what's most important, which is <em>practising speaking</em>. No complicated grammar lessons or endless vocabulary lists. Just the words and phrases you <em>actually need in your real life</em>, so you can practise saying them, improve your pronunciation and <em>feel confident being understood</em>.
                            </p>
                            <p style={{ margin: 0, fontSize: 17, lineHeight: 1.7, color: C.onDarkMuted }}>
                                If you feel nervous speaking, struggle with pronunciation, or feel like you know the words but cannot get them out of your mouth, <em>Mimique is for you</em>.
                            </p>
                            <div className="mq-hero-ctas" style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 10, flexWrap: 'wrap' }}>
                                <button
                                    className="mq-cta mq-arrowbtn"
                                    onClick={() => openAuth('signup')}
                                    style={{ ...CTA_PRIMARY, padding: '15px 38px' }}
                                >
                                    Get Started<span className="mq-arrow" aria-hidden="true">&rarr;</span>
                                </button>
                                {/* Same box metrics as CTA_PRIMARY so the two hero
                                    buttons sit at identical heights. */}
                                <a
                                    className="mq-cta mq-arrowbtn"
                                    href="#steps"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        lineHeight: 1,
                                        color: C.onDarkPrimary,
                                        fontFamily: F.body,
                                        fontSize: 17,
                                        fontWeight: 700,
                                        padding: '15px 32px',
                                        borderRadius: 9999,
                                        border: '1px solid rgba(255,255,255,0.22)',
                                        textDecoration: 'none',
                                    }}
                                >
                                    See how it works<span className="mq-arrow" aria-hidden="true">&rarr;</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* HOW IT WORKS */}
                <div id="steps" className="mq-section" style={{ background: '#ffffff', padding: '130px 24px' }}>
                    <div className="mq-railpad" style={{ maxWidth: 1160, margin: '0 auto' }}>
                        <SectionEyebrow>How it works</SectionEyebrow>
                        <h2 style={{ margin: '0 0 8px', fontFamily: F.heading, fontSize: 36, fontWeight: 800, color: C.deep }}>A simple 3-step listen, repeat, improve loop</h2>
                        <p style={{ margin: '0 0 40px', fontSize: 17, lineHeight: 1.6, color: C.bodyOnWhite2 }}>
                            Short, focused sessions designed to build real speaking confidence in as little as 5 minutes a day. You choose the words and phrases to practise, so every session is what you need in <em>your</em> life.
                        </p>
                        <div className="mq-stepgrid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 20 }}>
                            <StepCard
                                n={1}
                                title="Listen & Learn"
                                body="Hear native French pronunciation for any word or phrase you want to learn. Your words, your situations."
                                iconPath={<>
                                    <path d="M4 13v-1a8 8 0 0 1 16 0v1" stroke="#e9f4ec" strokeWidth="2" strokeLinecap="round" />
                                    <rect x="2.5" y="13" width="5" height="7" rx="2" fill="#e9f4ec" />
                                    <rect x="16.5" y="13" width="5" height="7" rx="2" fill="#e9f4ec" />
                                </>}
                            />
                            <StepCard
                                n={2}
                                title="Repeat & Record"
                                body="Repeat the sounds you hear while Mimique records your voice. Practise saying it until it feels natural."
                                iconPath={<>
                                    <rect x="9" y="2" width="6" height="12" rx="3" fill="#e9f4ec" />
                                    <path d="M5 11a7 7 0 0 0 14 0" stroke="#e9f4ec" strokeWidth="2" strokeLinecap="round" />
                                    <path d="M12 18v4M9 22h6" stroke="#e9f4ec" strokeWidth="2" strokeLinecap="round" />
                                </>}
                            />
                            <StepCard
                                n={3}
                                title="Compare & Improve"
                                body="Compare your pronunciation and get instant feedback. Keep practising and build real fluency over time."
                                iconPath={<>
                                    <rect x="3" y="14" width="4" height="7" rx="1" fill="#e9f4ec" />
                                    <rect x="10" y="9" width="4" height="12" rx="1" fill="#e9f4ec" />
                                    <rect x="17" y="4" width="4" height="17" rx="1" fill="#e9f4ec" />
                                </>}
                            />
                        </div>
                        <div style={{ marginTop: 48, textAlign: 'center' }}>
                            <button
                                className="mq-cta mq-arrowbtn"
                                onClick={() => openAuth('signup')}
                                style={{ ...CTA_PRIMARY, padding: '15px 38px' }}
                            >
                                Try it for free<span className="mq-arrow" aria-hidden="true">&rarr;</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* WHO IT'S FOR — outside the phone rail so it uses the full width */}
            <div id="features" className="mq-section" style={{ background: `linear-gradient(180deg, ${C.deep} 0%, ${C.darkest} 100%)`, padding: '130px 24px' }}>
                <div style={{ maxWidth: 1160, margin: '0 auto' }}>
                        <SectionEyebrow onDark>Who it's for</SectionEyebrow>
                        <h2 style={{ margin: '0 0 16px', fontFamily: F.heading, fontSize: 36, fontWeight: 700, color: '#ffffff' }}>
                            For people who want to speak French (and be understood!)
                        </h2>
                        <p style={{ margin: '0 0 48px', fontSize: 16, lineHeight: 1.6, color: C.onDarkMuted }}>
                            Practise the French you actually need for your real life, at your own pace, whatever your level.
                        </p>
                        <div className="mq-three-up" style={{ display: 'grid', gap: 20 }}>
                            {whoItsFor.map((item, i) => (
                                <div key={i} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 24, padding: '26px 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                            <circle cx="12" cy="12" r="11" fill={C.light} />
                                            <path d="M7 12.5l3 3 7-7" stroke={C.deep} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <div style={{ fontFamily: F.brand, fontSize: 17, fontWeight: 700, color: '#ffffff' }}>{item.title}</div>
                                    </div>
                                    <div style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(243,249,245,0.72)' }}>{item.body}</div>
                                </div>
                            ))}
                    </div>
                </div>
            </div>

            {/* TESTIMONIALS */}
            <div id="testimonials" className="mq-section" style={{ background: C.paleBg, padding: '130px 24px' }}>
                <div style={{ maxWidth: 1160, margin: '0 auto' }}>
                    <div style={{ maxWidth: 640, margin: '0 auto 56px', textAlign: 'center' }}>
                        <SectionEyebrow>Testimonials</SectionEyebrow>
                        <h2 style={{ margin: '0 0 16px', fontFamily: F.heading, fontSize: 38, fontWeight: 700, color: C.deep }}>
                            Learners who already found their French voice
                        </h2>
                        <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: C.bodyOnWhite }}>
                            Feedback from people practising their spoken French while helping shape Mimique. 💚
                        </p>
                    </div>
                    <TestimonialCarousel items={testimonials} />
                </div>
            </div>

            {/* ABOUT */}
            <div className="mq-section" style={{ background: '#ffffff', padding: '130px 24px' }}>
                <div className="mq-about" style={{ maxWidth: 1160, margin: '0 auto', display: 'grid', gap: 60, alignItems: 'center' }}>
                    <div className="mq-about-photo">
                        {/* The brand light green. C.bodyOnWhite was too close to
                            the body text to read as a caption. */}
                        <div style={{ marginBottom: 14, fontFamily: F.brand, fontSize: 15, fontWeight: 700, color: C.light, textAlign: 'center' }}>
                            Leanne O'Leary - Mimique Founder
                        </div>
                        <img
                            src="/assets/founder.jpg"
                            alt="Leanne O'Leary - Mimique Founder"
                            style={{ width: '100%', aspectRatio: '4 / 5', borderRadius: 32, objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <SectionEyebrow mb={0}>Mimique Founder</SectionEyebrow>
                        <h2 style={{ margin: 0, fontFamily: F.heading, fontSize: 34, fontWeight: 700, color: C.deep }}>About Me</h2>
                        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.8, color: C.bodyOnWhite }}>
                            I've lived in France for several years now. On paper, my French is solid. I have the DELF B2 certificate, a level above what's required for French citizenship, and enough to enrol in a French university. I can read, write and understand most of it. The problem is, I have textbook French, not conversational French. Since moving here, I've spent most of my time working from home, so I rarely get the chance to actually speak it day to day.
                        </p>
                        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.8, color: C.bodyOnWhite }}>
                            Put me in front of French people and I panic, stumble and make mistakes. The words are in my head, but I can't get them out quickly or clearly enough.
                        </p>
                        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.8, color: C.bodyOnWhite }}>
                            And then there's pronunciation. I have a strong northern English accent, and my pronunciation often gets in the way of being understood. In all the French classes I've taken, only one ever focused specifically on phonetics. I knew the words, but nobody had really taught me how to say them.
                        </p>
                        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.8, color: C.bodyOnWhite }}>
                            I'm a web application developer, and Mimique is the first thing I've ever built for myself, because honestly, I really need this app.
                        </p>
                        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.8, color: C.bodyOnWhite }}>
                            Mimique is for people like me who've put in the hours, who know more than they can say, and who are tired of feeling stupid in their second language. It's about practising the words and phrases you actually want to learn and getting them out of your head and into your mouth. Speaking without hesitation, one phrase at a time, until it feels less alien and more natural.
                        </p>
                        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.8, color: C.bodyOnWhite }}>
                            There's more on <Link to="/why-i-built-mimique" style={{ color: C.primary, fontWeight: 600 }}>why I built Mimique</Link>, and how the practice loop works, on the blog.
                        </p>
                    </div>
                </div>
            </div>

            {/* PRICING */}
            <div id="pricing" className="mq-section" style={{ background: C.paleBg, padding: '130px 24px' }}>
                <div style={{ maxWidth: 1160, margin: '0 auto' }}>
                    <div style={{ maxWidth: 900, margin: '0 auto 56px', textAlign: 'center' }}>
                        <SectionEyebrow>How much?</SectionEyebrow>
                        <h2 className="mq-oneline" style={{ margin: '0 0 20px', fontFamily: F.heading, fontSize: 38, fontWeight: 700, color: C.deep }}>
                            Start free. Upgrade when you're ready.
                        </h2>
                    </div>
                    <div className="mq-tiergrid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24, maxWidth: 1080, margin: '0 auto' }}>
                        <TierCard
                            name="Free"
                            price={PLAN_PRICES.free}
                            note="No card needed."
                            features={[
                                '30 phrases',
                                'Advanced pronunciation scoring*',
                                'In-depth phrase & word-level feedback*',
                                'Manual phrase entry (auto-translate)',
                                'Browser French voice',
                                'Support',
                            ]}
                            cta="Get started"
                            onCta={() => openAuth('signup')}
                        />
                        <TierCard
                            featured
                            name="Pro"
                            price={PLAN_PRICES.pro}
                            priceNote="/month"
                            note="Billed monthly. Cancel any time."
                            features={[
                                '500 phrases',
                                'In-depth phrase & word-level feedback*',
                                'Advanced pronunciation scoring*',
                                'Manual phrase entry (auto-translate)',
                                'AI phrase generation*',
                                '10 natural French voices',
                                'Priority support',
                                'Vote on upcoming features',
                            ]}
                            cta={canCheckout() ? 'Get Started' : null}
                            onCta={() => openAuth('signup', 'pro')}
                        />
                        <TierCard
                            name="Unlimited"
                            price={PLAN_PRICES.unlimited}
                            priceNote="/month"
                            note="Billed monthly. Cancel any time."
                            features={[
                                'Unlimited phrases',
                                'Unlimited in-depth phrase & word-level feedback*',
                                'Unlimited advanced pronunciation scoring*',
                                'Manual phrase entry (auto-translate)',
                                'Unlimited AI phrase generation*',
                                '20+ natural French voices',
                                'Priority support',
                                'Vote on upcoming features',
                            ]}
                            cta={canCheckout() ? 'Get started' : null}
                            onCta={() => openAuth('signup', 'unlimited')}
                        />
                    </div>
                    <p style={{ maxWidth: 1080, margin: '32px auto 0', fontSize: 13, lineHeight: 1.6, color: C.bodyOnWhite, textAlign: 'center' }}>
                        *Fair use limits apply to scoring, feedback and AI features on every plan.
                        Prices and limits are introductory and may change based on usage and running
                        costs.
                    </p>
                </div>
            </div>

            {/* FAQ */}
            <div id="faq" className="mq-section" style={{ background: '#ffffff', padding: '130px 24px' }}>
                <div style={{ maxWidth: 800, margin: '0 auto' }}>
                    <div style={{ maxWidth: 640, margin: '0 auto 56px', textAlign: 'center' }}>
                        <SectionEyebrow>FAQ</SectionEyebrow>
                        <h2 style={{ margin: 0, fontFamily: F.heading, fontSize: 38, fontWeight: 700, color: C.deep }}>
                            Questions people ask
                        </h2>
                    </div>
                    <FaqList faqs={faqs} />
                </div>
            </div>

            {/* BLOG. Every published post, fetched from the API and edited at
                /admin/blog, in a carousel. Hidden while they load, and hidden
                entirely if there is nothing published. */}
            {latestPosts.length > 0 && (
                <div id="blog" className="mq-section" style={{ background: C.paleBg, padding: '130px 24px' }}>
                    <div style={{ maxWidth: 1160, margin: '0 auto' }}>
                        <div style={{ maxWidth: 640, margin: '0 auto 56px', textAlign: 'center' }}>
                            <SectionEyebrow>Blog</SectionEyebrow>
                            <h2 style={{ margin: '0 0 16px', fontFamily: F.heading, fontSize: 38, fontWeight: 700, color: C.deep }}>
                                Notes from someone still learning
                            </h2>
                            <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: C.bodyOnWhite }}>
                                Freezing mid-conversation, pronunciation nobody teaches you, and what actually helped.
                                My experience, and what I found when I went looking.
                            </p>
                        </div>
                        <BlogCarousel posts={latestPosts} />
                        <div style={{ textAlign: 'center', marginTop: 36 }}>
                            <Link to="/blog" style={{ fontSize: 15, fontWeight: 700, color: C.primary, textDecoration: 'none' }}>
                                See all posts &rarr;
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {/* FINAL CTA. Shared with the blog posts, see SiteChrome. */}
            <SiteFinalCta label="Get Started" onCta={() => openAuth('signup')} />

            <SiteFooter />

            {/* AUTH MODAL */}
            {isAuthOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={handleCloseAuth}
                >
                    {/* stopPropagation so clicks inside the card don't reach the
                        backdrop's close handler */}
                    <Card
                        className="w-full max-w-md bg-card text-card-foreground shadow-2xl animate-fade-in relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={handleCloseAuth}
                            aria-label="Close"
                            className="absolute right-3 top-3 text-muted-foreground hover:text-foreground p-1 z-10"
                        >
                            <CloseIcon className="h-4 w-4" />
                        </button>

                        {authView === 'forgot-sent' && (
                            <>
                                <CardHeader className="text-center pb-2">
                                    <CardTitle className="text-2xl">Check your inbox</CardTitle>
                                    <CardDescription>
                                        If <strong>{forgotEmail}</strong> is registered, a reset link is on its way. Check your spam folder if you don't see it.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <Button variant="ghost" className="w-full" onClick={handleCloseAuth}>Close</Button>
                                </CardContent>
                            </>
                        )}

                        {authView === 'forgot' && (
                            <>
                                <CardHeader className="text-center pb-2">
                                    <CardTitle className="text-2xl">Reset password</CardTitle>
                                    <CardDescription>Enter your email and we'll send you a reset link</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleForgotPassword} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="forgot-email">Email</Label>
                                            <Input
                                                id="forgot-email"
                                                type="email"
                                                placeholder="your@email.com"
                                                value={forgotEmail}
                                                onChange={(e) => setForgotEmail(e.target.value)}
                                                required
                                                className="bg-secondary"
                                            />
                                        </div>
                                        <Button type="submit" className="w-full rounded-full bg-primary hover:bg-primary/90" disabled={authLoading}>
                                            {authLoading ? 'Sending...' : 'Send reset link'}
                                        </Button>
                                    </form>
                                    <Button variant="ghost" className="w-full mt-2" onClick={() => setAuthView('tabs')}>
                                        Back to sign in
                                    </Button>
                                </CardContent>
                            </>
                        )}

                        {authView === 'tabs' && (
                            <>
                                <CardHeader className="text-center pb-2">
                                    {!BETA_CLOSED && (
                                        <div className="flex gap-2 justify-center mb-3">
                                            <button
                                                onClick={() => setAuthTab('login')}
                                                className={`px-4 py-1.5 rounded-full text-sm font-semibold ${authTab === 'login' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground'}`}
                                            >
                                                Log in
                                            </button>
                                            <button
                                                onClick={() => setAuthTab('signup')}
                                                className={`px-4 py-1.5 rounded-full text-sm font-semibold ${authTab === 'signup' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground'}`}
                                            >
                                                Sign up
                                            </button>
                                        </div>
                                    )}
                                    <CardTitle className="text-2xl">
                                        {authTab === 'login' ? 'Welcome back' : 'Create your account'}
                                    </CardTitle>
                                    <CardDescription>
                                        {pendingPlan
                                            ? `We'll take you to checkout for ${pendingPlan === 'pro' ? 'Pro' : 'Unlimited'} straight after. Use this same email at checkout so your payment links to your account.`
                                            : authTab === 'login'
                                                ? 'Sign in to keep practising.'
                                                : "It's free. No card needed."}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {authTab === 'login' ? (
                                        <form onSubmit={handleLogin} className="space-y-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="login-email">Email</Label>
                                                <Input
                                                    data-testid="login-email"
                                                    id="login-email"
                                                    type="email"
                                                    inputMode="email"
                                                    autoComplete="username"
                                                    autoCapitalize="none"
                                                    autoCorrect="off"
                                                    spellCheck={false}
                                                    placeholder="your@email.com"
                                                    value={loginForm.email}
                                                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                                                    required
                                                    className="bg-secondary"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="login-password">Password</Label>
                                                <div className="relative">
                                                    <Input
                                                        data-testid="login-password"
                                                        id="login-password"
                                                        type={showPassword ? 'text' : 'password'}
                                                        autoComplete="current-password"
                                                        autoCapitalize="none"
                                                        autoCorrect="off"
                                                        spellCheck={false}
                                                        placeholder="••••••••"
                                                        value={loginForm.password}
                                                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                                                        required
                                                        className="bg-secondary pr-10"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowPassword(v => !v)}
                                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                                                    >
                                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    </button>
                                                </div>
                                            </div>
                                            <Button
                                                data-testid="login-submit"
                                                type="submit"
                                                className="w-full rounded-full bg-primary hover:bg-primary/90"
                                                disabled={authLoading}
                                            >
                                                {authLoading ? 'Signing in...' : 'Sign In'}
                                            </Button>
                                            <button
                                                type="button"
                                                className="w-full text-sm text-muted-foreground hover:text-foreground text-center mt-1"
                                                onClick={() => setAuthView('forgot')}
                                            >
                                                Forgot your password?
                                            </button>
                                        </form>
                                    ) : (
                                        <form onSubmit={handleSignup} className="space-y-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="signup-name">Your name</Label>
                                                <Input
                                                    id="signup-name"
                                                    type="text"
                                                    autoComplete="name"
                                                    placeholder="Your name"
                                                    value={signupForm.name}
                                                    onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                                                    required
                                                    className="bg-secondary"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="signup-email">Email</Label>
                                                <Input
                                                    id="signup-email"
                                                    type="email"
                                                    inputMode="email"
                                                    autoComplete="email"
                                                    autoCapitalize="none"
                                                    autoCorrect="off"
                                                    spellCheck={false}
                                                    placeholder="your@email.com"
                                                    value={signupForm.email}
                                                    onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                                                    required
                                                    className="bg-secondary"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="signup-password">Password</Label>
                                                <div className="relative">
                                                    <Input
                                                        id="signup-password"
                                                        type={showPassword ? 'text' : 'password'}
                                                        autoComplete="new-password"
                                                        autoCapitalize="none"
                                                        autoCorrect="off"
                                                        spellCheck={false}
                                                        placeholder="••••••••"
                                                        value={signupForm.password}
                                                        onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                                                        required
                                                        minLength={8}
                                                        className="bg-secondary pr-10"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowPassword(v => !v)}
                                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                                                    >
                                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    </button>
                                                </div>
                                            </div>
                                            <Button
                                                type="submit"
                                                className="w-full rounded-full bg-primary hover:bg-primary/90"
                                                disabled={authLoading}
                                            >
                                                {authLoading ? 'Creating account...' : 'Create account'}
                                            </Button>
                                        </form>
                                    )}
                                </CardContent>
                            </>
                        )}

                    </Card>
                </div>
            )}
        </div>
    );
};

export default Landing;
